import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { fetchData, postData } from '../../helpers/apiUtils';
import {
  DnvHotStock,
  DnvReserveResponse,
  DnvAssetAnalysisSend,
} from '../../helpers/dnvAssetAnalysisData';

const todayBerlin = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const DnvForm: FC = () => {
  const [hotStocks, setHotStocks] = useState<DnvHotStock[]>([]);
  const [stocksLoading, setStocksLoading] = useState(true);
  const [stocksError, setStocksError] = useState<string | null>(null);

  const [isin, setIsin] = useState('');
  const [sendDate, setSendDate] = useState(todayBerlin());
  const [mailingId, setMailingId] = useState('');
  const [additionalTracking, setAdditionalTracking] = useState('');
  const [emailPlaceholder, setEmailPlaceholder] = useState('[email]');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [reserved, setReserved] = useState<DnvReserveResponse | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const previewIframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStocksLoading(true);
      setStocksError(null);
      const result = await fetchData<DnvHotStock[]>('/api/dnv-asset-analysis/hot-stocks');
      if (cancelled) return;
      if (result.success && result.data) {
        setHotStocks(result.data);
        if (result.data.length > 0) setIsin(result.data[0].isin);
      } else {
        setStocksError(result.error || 'Fehler beim Laden der Hot-Stocks');
      }
      setStocksLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-render preview iframe when reserved html changes. `preview_html` is the
  // full email document (DOCTYPE/html/body, 600px shell, FT header) the
  // operator will paste into their ESP — write it verbatim, no double-wrap.
  useEffect(() => {
    const iframe = previewIframeRef.current;
    if (!iframe || !reserved) return;
    const doc = iframe.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(reserved.preview_html);
    doc.close();
  }, [reserved]);

  const submitDisabled = useMemo(() => {
    if (!isin) return true;
    if (!sendDate) return true;
    const m = parseInt(mailingId, 10);
    if (!Number.isFinite(m) || m <= 0) return true;
    if (!emailPlaceholder.trim()) return true;
    return submitting;
  }, [isin, sendDate, mailingId, emailPlaceholder, submitting]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    setActionMessage(null);
    const result = await postData<DnvReserveResponse>('/api/dnv-asset-analysis/reserve', {
      isin,
      send_date: sendDate,
      mailing_id: parseInt(mailingId, 10),
      additional_tracking: additionalTracking.trim() || null,
      email_placeholder: emailPlaceholder.trim(),
    });
    if (result.success && result.data) {
      setReserved(result.data);
    } else {
      setSubmitError(result.error || 'Fehler beim Reservieren der SAM');
    }
    setSubmitting(false);
  };

  const handleCopy = async () => {
    if (!reserved) return;
    try {
      await navigator.clipboard.writeText(reserved.send.final_html);
      setActionMessage('HTML in die Zwischenablage kopiert.');
    } catch {
      setActionMessage('Kopieren fehlgeschlagen — bitte manuell aus dem Textfeld kopieren.');
    }
  };

  const handleConfirm = async () => {
    if (!reserved) return;
    setActionMessage(null);
    const result = await postData<DnvAssetAnalysisSend>(
      `/api/dnv-asset-analysis/sends/${reserved.send.id}/confirm`,
      {},
    );
    if (result.success && result.data) {
      setReserved({ ...reserved, send: result.data });
      setActionMessage('Send als versendet markiert.');
    } else {
      setActionMessage(result.error || 'Confirm fehlgeschlagen.');
    }
  };

  const handleCancel = async () => {
    if (!reserved) return;
    setActionMessage(null);
    const result = await postData<DnvAssetAnalysisSend>(
      `/api/dnv-asset-analysis/sends/${reserved.send.id}/cancel`,
      {},
    );
    if (result.success && result.data) {
      setReserved({ ...reserved, send: result.data });
      setActionMessage('Send als verworfen markiert.');
    } else {
      setActionMessage(result.error || 'Cancel fehlgeschlagen.');
    }
  };

  const handleNewMailing = () => {
    setReserved(null);
    setActionMessage(null);
    setSubmitError(null);
  };

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Auswahl
          </Typography>
          {stocksError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {stocksError}
            </Alert>
          )}
          <Stack spacing={2}>
            <TextField
              label="Aktie (Top 40 Hot-Stocks heute)"
              select
              value={isin}
              onChange={(e) => setIsin(e.target.value)}
              disabled={stocksLoading || hotStocks.length === 0}
              SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 480 } } } }}
              fullWidth
              required
            >
              {stocksLoading && <MenuItem value="">Lade Hot-Stocks…</MenuItem>}
              {hotStocks.map((s) => (
                <MenuItem key={s.isin} value={s.isin}>
                  {s.name} — {s.isin} (Score {s.score.toFixed(1)})
                </MenuItem>
              ))}
            </TextField>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Senddatum"
                type="date"
                value={sendDate}
                onChange={(e) => setSendDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />
              <TextField
                label="Mailing ID"
                type="number"
                value={mailingId}
                onChange={(e) => setMailingId(e.target.value)}
                inputProps={{ min: 1 }}
                fullWidth
                required
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Zusätzliches Tracking (optional)"
                value={additionalTracking}
                onChange={(e) => setAdditionalTracking(e.target.value)}
                fullWidth
              />
              <TextField
                label="E-Mail Platzhalter"
                value={emailPlaceholder}
                onChange={(e) => setEmailPlaceholder(e.target.value)}
                helperText="ESP-Mergetag, z.B. [email] oder %recipient.email%"
                fullWidth
                required
              />
            </Stack>
            {submitError && <Alert severity="error">{submitError}</Alert>}
            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={submitDisabled}
                startIcon={submitting ? <CircularProgress size={16} /> : null}
              >
                {submitting ? 'Generiere…' : 'Vorschau & HTML erzeugen'}
              </Button>
              {reserved && (
                <Button sx={{ ml: 2 }} variant="outlined" onClick={handleNewMailing}>
                  Neues Mailing
                </Button>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {reserved && (
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={2} flexWrap="wrap">
              <Typography variant="h6">
                Send #{reserved.send.id} — {reserved.send.asset_name}
              </Typography>
              <Chip
                label={statusLabel(reserved.send.status)}
                color={statusColor(reserved.send.status)}
                size="small"
              />
              <Box flexGrow={1} />
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopy}
              >
                HTML kopieren
              </Button>
              {reserved.send.status === 'prepared' && (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleConfirm}
                  >
                    Als versendet markieren
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                  >
                    Verwerfen
                  </Button>
                </>
              )}
            </Stack>
            {actionMessage && (
              <Alert severity="info" sx={{ mb: 2 }} onClose={() => setActionMessage(null)}>
                {actionMessage}
              </Alert>
            )}

            <Typography variant="subtitle2">Betreff</Typography>
            <Typography variant="body2" mb={2} sx={{ fontFamily: 'monospace' }}>
              {reserved.send.subject || '—'}
            </Typography>

            <Typography variant="subtitle2">LP URL</Typography>
            <Typography
              variant="body2"
              mb={2}
              sx={{
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                background: '#f4f4f4',
                p: 1,
                borderRadius: 1,
              }}
            >
              {reserved.send.lp_url}
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box flex={1}>
                <Typography variant="subtitle2" mb={1}>
                  HTML (zum Kopieren)
                </Typography>
                <TextField
                  multiline
                  fullWidth
                  minRows={20}
                  maxRows={40}
                  value={reserved.send.final_html}
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
                />
              </Box>
              <Box flex={1}>
                <Typography variant="subtitle2" mb={1}>
                  Vorschau
                </Typography>
                <Box
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    overflow: 'hidden',
                    height: 720,
                    background: '#fff',
                  }}
                >
                  <iframe
                    ref={previewIframeRef}
                    title="DNV SAM Vorschau"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  />
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

const statusLabel = (s: DnvAssetAnalysisSend['status']): string => {
  if (s === 'prepared') return 'Reserviert';
  if (s === 'confirmed') return 'Versendet';
  return 'Verworfen';
};
const statusColor = (
  s: DnvAssetAnalysisSend['status'],
): 'default' | 'success' | 'warning' | 'error' => {
  if (s === 'confirmed') return 'success';
  if (s === 'cancelled') return 'error';
  return 'warning';
};

export default DnvForm;
